import React, {useState, useMemo, useRef} from 'react'
import {remCalc} from '../../theme/_utils'

import PropTypes from 'prop-types'
import {
    Flex,
    Text,
    useStyleConfig,
    Button,
    Popover,
    PopoverHeader,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverFooter,
    IconButton,
    Badge,
    Stack,
    Box,
    HStack,
    useMediaQuery,
    Image,
} from '@chakra-ui/react'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'

import {CartIcon} from '../icons'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useIntl, FormattedNumber, FormattedMessage} from 'react-intl'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {InfoIcon} from '../../components/icons'
import InfoPopover from '../popovers/info-popover'
import PromoPopover from '@salesforce/retail-react-app/app/components/promo-popover'

// Utilities
import {useProducts} from '@salesforce/commerce-sdk-react'
import OmnibusModalPrice from '../display-price/omnibus/omnibus-modal-price'
import SubscriptionItem from '../subscription-item'
import {useBasketTotalItems} from '../../hooks/use-basket-total-items'
import PaymentMethodLabel from '../payment-method-label'

const MiniCartPopover = ({onMyCartClick = noop}) => {
    const styles = useStyleConfig('MiniCartPopover', {})
    const [isLargeScreen] = useMediaQuery(`(min-width: ${remCalc(1024)})`)
    const headerRef = useRef(null)
    const [isPopoverOpen, setPopoverOpen] = useState(false)
    const closeTimer = useRef()

    const openMiniCartAndFocus = () => {
        setPopoverOpen(true)
        setTimeout(() => {
            headerRef.current?.focus()
        }, 0)
    }

    const handleClick = (e) => {
        // If cart is empty or it's a mouse click, navigate to cart page
        if (!basket?.productItems || basket.productItems.length === 0) {
            onMyCartClick('base')
        } else if (e.detail === 0) {
            openMiniCartAndFocus()
        } else {
            onMyCartClick('base')
        }
    }

    const intl = useIntl()
    const {data: basket} = useCurrentBasket()
    const totalItems = useBasketTotalItems()

    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''

    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                expand: ['availability', 'images', 'prices', 'promotions'],
                perPricebook: true,
            },
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            },
        },
    )

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                select: '(data.(id,inventory,type))',
            },
            headers: {
                'x-debug': 'minicart-index',
            },
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            },
        },
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory,
                        },
                    }
                }
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    /*****************Basket Mutation************************/

    const [localQuantity] = useState({})

    const allSubscriptionsDetails = basket?.c_allSubscriptionsDetails
        ? JSON.parse(basket.c_allSubscriptionsDetails)
        : {}

    const popoverContentRef = useRef(null)
    // Hover handlers with timer
    const handleMouseEnter = () => {
        clearTimeout(closeTimer.current)
        setPopoverOpen(true)
    }
    const handleMouseLeave = () => {
        closeTimer.current = setTimeout(() => setPopoverOpen(false), 100)
    }

    return (
        <Popover
            isLazy
            strategy="fixed"
            placement="bottom-end"
            isOpen={basket?.productItems && basket?.productItems.length > 0 ? isPopoverOpen : false}
            onClose={() => setPopoverOpen(false)}
        >
            <PopoverTrigger>
                <IconButton
                    aria-label={intl.formatMessage(
                        {
                            id: 'header.button.assistive_msg.my_cart_with_num_items',
                            defaultMessage: 'My cart, number of items: {numItems}',
                        },
                        {numItems: totalItems || 0},
                    )}
                    icon={
                        <>
                            <CartIcon sx={styles.cartIcon} />
                            {basket && totalItems > 0 && (
                                <Badge
                                    data-testid="cart-item-count"
                                    variant="notification"
                                    {...styles.badge}
                                >
                                    {totalItems}
                                </Badge>
                            )}
                        </>
                    }
                    data-testid="cart-icon"
                    variant="unstyled"
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    {...styles.buttonIcons}
                />
            </PopoverTrigger>

            <PopoverContent
                ref={popoverContentRef}
                data-testid="minicart-content"
                {...styles.popoverContainer}
                {...styles.popoverContent}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {basket?.productItems && basket?.productItems.length > 0 && isLargeScreen && (
                    <>
                        <PopoverHeader {...styles.miniCartHeader} tabIndex={0} ref={headerRef}>
                            <Text {...styles.mediumText} data-testid="product-name">
                                {intl.formatMessage({
                                    id: 'mini_cart.header.title1',
                                    defaultMessage: 'You have',
                                })}{' '}
                                <Text as="span" {...styles.total}>
                                    {intl.formatMessage(
                                        {
                                            id: 'mini_cart.header.title2',
                                            defaultMessage: '{numItems}  items',
                                        },
                                        {numItems: totalItems},
                                    )}{' '}
                                </Text>
                                {intl.formatMessage({
                                    id: 'mini_cart.header.title3',
                                    defaultMessage: 'in your cart',
                                })}
                            </Text>
                        </PopoverHeader>
                        <PopoverBody
                            {...styles.popoverBody}
                            sx={{
                                maxHeight: basket?.productItems?.length > 2 ? '340px' : 'auto',
                                overflowY: basket?.productItems?.length > 2 ? 'scroll' : 'visible',
                            }}
                            data-testid="minicart-body"
                        >
                            <Stack {...styles.bodyStack}>
                                {Object.entries(allSubscriptionsDetails)?.map(
                                    ([bundleId, details], i) => (
                                        <SubscriptionItem
                                            key={`${bundleId}-${i}`}
                                            details={details}
                                            variant={'subscriptionsMiniCart'}
                                        />
                                    ),
                                )}
                                {basket?.productItems?.map((productItem) => {
                                    if (productItem?.c_isSubscription === 'true') return
                                    const officinaImage = productItem?.c_officinaConfigurationImage
                                    const officinaEditParams =
                                        productItem?.c_officinaConfigurationId

                                    const variant = {
                                        ...productItem,
                                        ...(productsByItemId &&
                                            productsByItemId[productItem.itemId]),
                                        isProductUnavailable: !isProductsLoading
                                            ? !productsByItemId?.[productItem.itemId]
                                            : undefined,
                                        price: productItem?.price,
                                        quantity: localQuantity[productItem.itemId]
                                            ? localQuantity[productItem.itemId]
                                            : productItem.quantity,
                                    }
                                    return (
                                        <ItemVariantProvider
                                            key={productItem?.productId}
                                            variant={variant}
                                        >
                                            <Flex {...styles.flexBody}>
                                                {officinaImage && officinaEditParams ? (
                                                    <Image
                                                        alt={officinaEditParams}
                                                        src={officinaImage}
                                                        ignoreFallback={true}
                                                        {...styles.imageBox}
                                                    />
                                                ) : (
                                                    <CartItemVariantImage
                                                        {...styles.imageBox}
                                                        imageProps={styles.imageProps}
                                                    />
                                                )}
                                                <Stack {...styles.namePriceQuantity}>
                                                    <CartItemVariantName
                                                        {...styles.productHeading}
                                                    />
                                                    <OmnibusModalPrice
                                                        product={variant}
                                                        addToCartView={true}
                                                        isMinicart={true}
                                                    />
                                                    <Text {...styles.quantity}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'mini_cart.item.quantity',
                                                                defaultMessage:
                                                                    'Quantity: {quantity}',
                                                            },
                                                            {quantity: productItem?.quantity},
                                                        )}
                                                    </Text>
                                                </Stack>
                                            </Flex>
                                        </ItemVariantProvider>
                                    )
                                })}
                            </Stack>
                        </PopoverBody>
                        <PopoverFooter {...styles.popoverFooter}>
                            <Flex {...styles.informationContainer}>
                                <Box>
                                    <HStack gap={remCalc(8)}>
                                        <Text {...styles?.subtotalSection?.shippingName}>
                                            {intl.formatMessage({
                                                defaultMessage: 'Delivery',
                                                id: 'order_summary.label.shipping',
                                            })}
                                        </Text>
                                        <Box {...styles?.subtotalSection?.infoIconContainer}>
                                            <InfoPopover
                                                variant="top-right"
                                                trigger={
                                                    <InfoIcon
                                                        {...styles?.subtotalSection?.infoIcon}
                                                    />
                                                }
                                                title={intl.formatMessage({
                                                    id: 'order_summary.delivery.info.title',
                                                    defaultMessage: 'Delivery Information',
                                                })}
                                                description={intl.formatMessage({
                                                    id: 'order_summary.delivery.info.description',
                                                    defaultMessage:
                                                        'Delivery details and estimated arrival time',
                                                })}
                                            />
                                        </Box>
                                    </HStack>
                                    <PaymentMethodLabel basket={basket} />
                                </Box>
                                {basket?.shippingItems?.[0]?.priceAdjustments?.length > 0 && (
                                    <PromoPopover ml={1}>
                                        <Stack>
                                            {basket?.shippingItems?.[0]?.priceAdjustments?.map(
                                                (adjustment) => (
                                                    <Text
                                                        key={adjustment.priceAdjustmentId}
                                                        fontSize="sm"
                                                    >
                                                        {adjustment.itemText}
                                                    </Text>
                                                ),
                                            )}
                                        </Stack>
                                    </PromoPopover>
                                )}
                                {basket?.shippingTotal > 0 ? (
                                    <Text {...styles?.subtotalSection?.priceAdjustment}>
                                        <FormattedNumber
                                            value={basket.shippingTotal}
                                            style="currency"
                                            currency={basket?.currency}
                                        />
                                    </Text>
                                ) : (
                                    <Text {...styles?.subtotalSection?.freeShippingText}>
                                        <FormattedMessage
                                            defaultMessage="Free shipping"
                                            id="order_summary.label.free_shipping"
                                        />
                                    </Text>
                                )}
                            </Flex>
                            <Flex {...styles.informationContainer}>
                                <HStack {...styles.priceStack}>
                                    <Text {...styles.largeText} {...styles.total}>
                                        {intl.formatMessage({
                                            id: 'mini_cart.total',
                                            defaultMessage: 'TOTAL',
                                        })}
                                    </Text>
                                    <Text {...styles.vat}>
                                        {intl.formatMessage({
                                            id: 'mini_cart.vat',
                                            defaultMessage: 'VAT Included (22%)',
                                        })}
                                    </Text>
                                </HStack>
                                <Box {...styles.largeText} {...styles.total}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket?.currency}
                                        value={basket?.orderTotal || basket?.productTotal}
                                    />
                                </Box>
                            </Flex>
                            <Stack {...styles.buttonStack}>
                                <Button
                                    data-testid="go-to-cart-button"
                                    variant="primary"
                                    onClick={async () => {
                                        onMyCartClick()
                                    }}
                                >
                                    {intl.formatMessage({
                                        id: 'mini_cart.go_to_cart',
                                        defaultMessage: 'Go to Cart',
                                    })}
                                </Button>
                            </Stack>
                        </PopoverFooter>
                    </>
                )}
            </PopoverContent>
        </Popover>
    )
}

MiniCartPopover.propTypes = {
    onMyCartClick: PropTypes.func,
}

export default MiniCartPopover
